'use client'

/**
 * usePrintAction — routes "system print" gestures into the page's own print
 * flow, so users who reach for Cmd/Ctrl+P (or the browser's menu print on
 * mobile) get the prepared print packet instead of a raw screen dump.
 *
 * Three legs, all optional except the shortcut:
 *
 * 1. Cmd/Ctrl+P is intercepted and `onPrintRequest` runs the same flow as the
 *    page's Print button (open the picker dialog, stage the packet, etc.).
 * 2. A print started from the browser menu / mobile share sheet cannot be
 *    cancelled, but Chromium fires `beforeprint` before it snapshots the page
 *    and synchronous DOM changes ARE captured. Pages whose print content
 *    renders directly from state can stage it in `onSystemPrintStart` (wrap
 *    the state update in `flushSync` from react-dom) and return true.
 *    ANDROID CAVEAT: Chrome for Android fires `afterprint` immediately after
 *    `beforeprint` — before its print framework reads the page — so a page
 *    that clears its print state on `afterprint` tears the content down and
 *    prints blank. This bites the page's own button-triggered
 *    `window.print()` too, not just system prints. On pages that must print
 *    correctly from Android, keep the packet AMBIENTLY MOUNTED in a
 *    print-only container (see ordo's shopping-list.tsx /
 *    cafeteria/serve.tsx), and never unmount print content directly in an
 *    `afterprint` handler — use `listenAfterPrintTeardown` below.
 * 3. When a system print ran that the page neither initiated (`printing`) nor
 *    staged (leg 2), `onUnhandledSystemPrint` fires after the dialog closes —
 *    the moment to open the real print dialog or toast a pointer at it.
 *    (Android's early `afterprint` means this can fire while the print UI is
 *    still up — a dialog opened here is simply waiting when the user returns.)
 */
import { useEffect, useRef } from 'react'

/**
 * How long after `afterprint` before it is safe to unmount print-only
 * content. Chrome for Android fires `afterprint` immediately when a print
 * starts — its print framework reads the page AFTER the event, so a page
 * that unmounts its packet in an `afterprint` handler prints blank pages
 * on Android. This applies to the page's OWN button-triggered
 * `window.print()` too, not just system prints. Desktop fires `afterprint`
 * when the dialog closes, so the delay merely holds an invisible
 * print-only portal a few seconds longer.
 */
export const AFTER_PRINT_TEARDOWN_DELAY_MS = 5_000

/** Grace period after the tab becomes visible again before tearing down. */
export const AFTER_PRINT_REVISIBLE_GRACE_MS = 1_000

/** The two listener methods the teardown needs — lets tests pass a fake. */
export type AfterPrintTarget = Pick<Window, 'addEventListener' | 'removeEventListener'>

/** The slice of `document` the visibility hold needs — fakeable in tests. */
export interface AfterPrintVisibilityDoc {
  visibilityState: DocumentVisibilityState
  addEventListener: (type: 'visibilitychange', listener: () => void) => void
  removeEventListener: (type: 'visibilitychange', listener: () => void) => void
}

/**
 * Listen for `afterprint` and run `teardown` only once it is safe:
 *
 *   afterprint → wait `delayMs` → if the tab is HIDDEN (Android's print UI
 *   backgrounds the page, and its framework can RE-READ the page while the
 *   preview is open — a paper-size change re-renders), hold until the tab is
 *   visible again, then tear down after a short grace.
 *
 * Repeated prints reset the sequence. Returns a cleanup that removes every
 * listener and cancels any pending teardown — return it from the owning
 * effect.
 */
export function listenAfterPrintTeardown(
  teardown: () => void,
  {
    delayMs = AFTER_PRINT_TEARDOWN_DELAY_MS,
    graceMs = AFTER_PRINT_REVISIBLE_GRACE_MS,
    target,
    doc,
  }: {
    delayMs?: number
    graceMs?: number
    target?: AfterPrintTarget
    doc?: AfterPrintVisibilityDoc
  } = {},
): () => void {
  const listenTarget: AfterPrintTarget | null = target ?? (typeof window === 'undefined' ? null : window)
  if (!listenTarget) {
    return () => {}
  }
  const visibilityDoc: AfterPrintVisibilityDoc | null = doc ?? (typeof document === 'undefined' ? null : document)

  let timer: ReturnType<typeof setTimeout> | null = null
  let waitingForVisible = false

  const clearPending = () => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    if (waitingForVisible && visibilityDoc) {
      visibilityDoc.removeEventListener('visibilitychange', onVisible)
      waitingForVisible = false
    }
  }

  const onVisible = () => {
    if (!visibilityDoc || visibilityDoc.visibilityState !== 'visible') {
      return
    }
    visibilityDoc.removeEventListener('visibilitychange', onVisible)
    waitingForVisible = false
    timer = setTimeout(teardown, graceMs)
  }

  const attempt = () => {
    timer = null
    if (visibilityDoc && visibilityDoc.visibilityState === 'hidden') {
      waitingForVisible = true
      visibilityDoc.addEventListener('visibilitychange', onVisible)
      return
    }
    teardown()
  }

  const onAfterPrint = () => {
    clearPending()
    timer = setTimeout(attempt, delayMs)
  }

  listenTarget.addEventListener('afterprint', onAfterPrint)
  return () => {
    listenTarget.removeEventListener('afterprint', onAfterPrint)
    clearPending()
  }
}

/** Keyboard events carry more than we need — keep the predicate testable. */
export type PrintShortcutKeys = Pick<
  KeyboardEvent,
  'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'repeat' | 'defaultPrevented'
>

/**
 * Plain Cmd/Ctrl+P only. Shift is excluded on purpose: Ctrl+Shift+P is
 * Firefox's private-window shortcut and Chrome's "system print dialog"
 * escape hatch — both must keep working. Alt combos are not print intents.
 */
export function isPrintShortcut(event: PrintShortcutKeys): boolean {
  if (event.defaultPrevented || event.repeat) {
    return false
  }
  if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
    return false
  }
  return event.key === 'p' || event.key === 'P'
}

export interface UsePrintActionOptions {
  /** Run the page's print flow (same as its Print button). Fired on Cmd/Ctrl+P. */
  onPrintRequest: () => void
  /**
   * True while the page's own print flow has content staged (e.g. its
   * `printTarget` state is set). Suppresses the system-print legs so the
   * page's own `window.print()` isn't treated as unhandled.
   */
  printing?: boolean
  /**
   * Synchronously stage print content when a system print starts outside the
   * app flow. Must be synchronous — wrap React state updates in `flushSync`.
   * Return true when content was staged; false/undefined marks the print
   * unhandled.
   */
  onSystemPrintStart?: () => boolean
  /** A system print ran that the page didn't initiate or stage. Fires after the print dialog closes. */
  onUnhandledSystemPrint?: () => void
  /** Set false to detach all listeners (e.g. while the page has nothing to print). */
  enabled?: boolean
}

export function usePrintAction({
  onPrintRequest,
  printing = false,
  onSystemPrintStart,
  onUnhandledSystemPrint,
  enabled = true,
}: UsePrintActionOptions): void {
  // Refs so the listeners are attached once per `enabled` flip, never per render.
  const printingRef = useRef(printing)
  printingRef.current = printing
  const callbacksRef = useRef({ onPrintRequest, onSystemPrintStart, onUnhandledSystemPrint })
  callbacksRef.current = { onPrintRequest, onSystemPrintStart, onUnhandledSystemPrint }

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return
    }

    function onKeyDown(event: KeyboardEvent) {
      if (!isPrintShortcut(event)) {
        return
      }
      event.preventDefault()
      callbacksRef.current.onPrintRequest()
    }

    // Tracks a print the page didn't initiate/stage, between beforeprint and
    // afterprint, so the unhandled callback fires once per print.
    let unhandledSystemPrint = false

    function onBeforePrint() {
      if (printingRef.current) {
        return
      }
      const staged = callbacksRef.current.onSystemPrintStart?.() ?? false
      unhandledSystemPrint = !staged
    }

    function onAfterPrint() {
      if (!unhandledSystemPrint) {
        return
      }
      unhandledSystemPrint = false
      const notify = callbacksRef.current.onUnhandledSystemPrint
      if (notify) {
        // Let the browser finish tearing down its print UI before the page
        // opens dialogs/toasts of its own.
        window.setTimeout(notify, 0)
      }
    }

    // Capture phase so an open dialog's own keydown handling can't swallow it.
    window.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('beforeprint', onBeforePrint)
    window.addEventListener('afterprint', onAfterPrint)
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('beforeprint', onBeforePrint)
      window.removeEventListener('afterprint', onAfterPrint)
    }
  }, [enabled])
}
